import { V1, V3 } from '@wayfinder/rms-sdk';
import xior, { merge } from 'xior';
import * as Request from '../request';
import * as Config from '../config';

// --- Stage-to-domain mapping (mirrors rms-sdk internals) ---

const getDomainName = stage => {
    if (stage === 'staging') return 'https://staging.rms.locuslabs.com';
    if (stage === 'dev') return 'https://dev.rms.locuslabs.com';
    return 'https://rms.locuslabs.com';
};

const getV1BaseURL = stage => {
    return `${getDomainName(stage)}/v3/v1`;
};

const getV3BaseURL = stage => {
    return `${getDomainName(stage)}/v3`;
};

// --- Response adapter: xior (rms-sdk) → superagent (request.js) ---

const adaptResponse = xiorResponse => {
    const adapted = {
        statusCode: xiorResponse.status,
        body: xiorResponse.data,
        text: typeof xiorResponse.data === 'string' ? xiorResponse.data : JSON.stringify(xiorResponse.data),
    };
    if (xiorResponse.status < 200 || xiorResponse.status >= 300) {
        const detail =
            adapted.body?.errors?.[0]?.detail ||
            adapted.body?.message ||
            `Request failed with status ${xiorResponse.status}`;
        const error = new Error(detail);
        error.status = xiorResponse.status;
        error.response = adapted;
        throw error;
    }
    return adapted;
};

const wrapMethod =
    fn =>
    async (...args) =>
        adaptResponse(await fn(...args));

// --- Inline xior client for 6 missing V1 methods ---

const createV1XiorInstance = (token, stage) => {
    const baseURL = getV1BaseURL(stage);
    const instance = xior.create({ baseURL });
    instance.interceptors.request.use(requestConfig =>
        merge(requestConfig, {
            headers: { 'x-api-key': token },
        }),
    );
    // Mimic validateStatus: () => true — never throw on non-2xx responses
    instance.interceptors.response.use(
        response => response,
        error => {
            if (error?.response) return error.response;
            return Promise.reject(error);
        },
    );
    return instance;
};

const createMissingV1Methods = (token, stage) => {
    const client = createV1XiorInstance(token, stage);
    return {
        createPOI: (venue, poi) => client.post(`/venues/${venue}/pois`, poi),
        getContentVenueAccountTree: category => client.get(`/category/${category}`),
        listPOIs: venue => client.get(`/venues/${venue}/pois`),
        openLocation: (venue, obj) => client.post(`/venues/${venue}/feature`, obj),
        updatePOIs: (venue, changes) => client.patch(`/venues/${venue}/pois`, changes),
        generateSpritesheet: (venueCategory, { venue, account } = {}) =>
            client.get(`/spritesheet/${venueCategory}`, { params: { venue, account } }),
    };
};

// --- Composite client factory ---

const rmsClient = async (stage = Config.getStage()) => {
    const token = await Config.getAccessToken();
    const v1 = V1({ token, baseURL: getV1BaseURL(stage) });
    const v3 = V3({ token, stage, baseURL: getV3BaseURL(stage) });
    const missing = createMissingV1Methods(token, stage);

    return {
        // V1 methods
        getAiJson: wrapMethod(v1.getAiJson),
        getBoundingBoxes: wrapMethod(v1.getBoundingBoxes),
        getBoundsPolygons: wrapMethod(v1.getBoundsPolygons),
        getContent: wrapMethod(v1.getContent),
        getGeoJson: wrapMethod(v1.getGeoJson),
        getVenueDataObject: wrapMethod(v1.getVenueDataObject),
        listContent: wrapMethod(v1.listContent),

        // V3 methods
        getFeatures: wrapMethod(v3.getFeatures),
        putFeaturesAndEntities: wrapMethod(v3.putFeaturesAndEntities),
        listVenueIds: wrapMethod(v3.listVenueIds),
        venueIdIsMigrated: wrapMethod(v3.venueIdIsMigrated),
        listVenuesForAccount: wrapMethod(v3.listVenuesForAccount),

        // Missing V1 methods (6) — inline xior
        createPOI: wrapMethod(missing.createPOI),
        getContentVenueAccountTree: wrapMethod(missing.getContentVenueAccountTree),
        listPOIs: wrapMethod(missing.listPOIs),
        openLocation: wrapMethod(missing.openLocation),
        updatePOIs: wrapMethod(missing.updatePOIs),
        generateSpritesheet: wrapMethod(missing.generateSpritesheet),
    };
};

const createPoiRequest = Request.makeRequest(rmsClient, 'createPOI');
const getAiJsonRequest = Request.makeRequest(rmsClient, 'getAiJson', Request.parseAiJson);
const getBoundingBoxesRequest = Request.makeRequest(rmsClient, 'getBoundingBoxes');
const getBoundsPolygonsRequest = Request.makeRequest(rmsClient, 'getBoundsPolygons');
const getContentRequest = Request.makeRequest(rmsClient, 'getContent');
const getContentVenueAccountTreeRequest = Request.makeRequest(rmsClient, 'getContentVenueAccountTree');
const getGeoJsonRequest = Request.makeRequest(rmsClient, 'getGeoJson', Request.body);
const listContentRequest = Request.makeRequest(rmsClient, 'listContent');
const listPoisRequest = Request.makeRequest(rmsClient, 'listPOIs');
const openLocationRequest = Request.makeRequest(rmsClient, 'openLocation');
const updatePoisRequest = Request.makeRequest(rmsClient, 'updatePOIs');
const listVenuesForAccountRequest = Request.makeRequest(rmsClient, 'listVenuesForAccount');
const generateSpritesheetRequest = Request.makeRequest(rmsClient, 'generateSpritesheet');
const getVenueDataObjectRequest = Request.makeRequest(rmsClient, 'getVenueDataObject');
const listVenueIdsRequest = Request.makeRequest(rmsClient, 'listVenueIds');
const putFeaturesAndEntitiesRequest = Request.makeRequestWithPolicy(
    rmsClient,
    'putFeaturesAndEntities',
    Request.retryWithBreaker,
);
const getFeaturesRequest = Request.makeRequest(rmsClient, 'getFeatures', Request.body);

const getEntitiesRequest = async (venueId, { accountId, format, locale, baseVenueId }) => {
    const stage = Config.getStage();
    const token = await Config.getAccessToken();
    const venueIdForRequest = locale !== 'en' && baseVenueId ? baseVenueId : venueId;

    try {
        const response = await V3({ token, stage, baseURL: getV3BaseURL(stage) }).getEntities(venueIdForRequest, {
            accountId,
            format,
            locale,
        });
        if (response.status === 200) {
            const responseData = Request.axiosData(response);
            const entitiesWithOverrides = applyLocaleOverrides(
                responseData.data.entities,
                responseData.data.localeOverrides,
                venueId,
            );
            return { entities: entitiesWithOverrides };
        }
    } catch (e) {
        Request.failure(e);
    }
};

export const applyLocaleOverrides = (entities, localeOverrides, venueIdOverride) => {
    if (!Array.isArray(entities) || !Array.isArray(localeOverrides)) return entities;

    const overrideFields = ['name', 'description', 'tags', 'displayTags', 'url', 'terminal', 'gate'];

    // Build a lookup map for overrides by entityId
    const overridesById = {};
    for (const override of localeOverrides) {
        if (override.entityId) {
            overridesById[override.entityId] = override;
        }
    }

    return entities.map(entity => {
        const override = overridesById[entity.entityId];
        if (!override) return entity;

        // Only override specified fields if the override value is not null/undefined
        const updatedFields = {};
        for (const field of overrideFields) {
            if (override[field] != null) {
                updatedFields[field] = override[field];
            }
        }

        // Always override venueId if provided
        if (venueIdOverride !== undefined) {
            updatedFields.venueId = venueIdOverride;
        }

        return { ...entity, ...updatedFields };
    });
};

const Rms = {
    createPOI: (id, entity) => createPoiRequest(id, entity),
    getBoundsPolygons: (venueId, opts) => getBoundsPolygonsRequest(venueId, opts),
    getBoundingBoxes: (venueId, opts) => getBoundingBoxesRequest(venueId, opts),
    getGeoJson: (id, levelId) => getGeoJsonRequest(id, levelId),
    getVenue: (venueId, format) => getVenueDataObjectRequest(venueId, Config.VENUE_OBJECT_TYPES.VENUE, { format }),
    generateSpritesheet: (c, venueId, accountId) =>
        generateSpritesheetRequest(c, { venue: venueId, account: accountId }),
    listPOIs: venueId => listPoisRequest(venueId),
    openLocation: (id, obj) => openLocationRequest(id, obj),
    updatePOIs: (venueId, changes) => updatePoisRequest(venueId, changes),
    getAiJson: (venueId, format) => getAiJsonRequest(venueId, format),
    getContentVenueAccountTree: category => getContentVenueAccountTreeRequest(category),
    getVenueStructures: (venueId, format) =>
        getVenueDataObjectRequest(venueId, Config.VENUE_OBJECT_TYPES.STRUCTURE, { format }),
    getVenueLevels: (venueId, format) =>
        getVenueDataObjectRequest(venueId, Config.VENUE_OBJECT_TYPES.LEVEL, { format }),
    getVenueOrdinals: (venueId, format) =>
        getVenueDataObjectRequest(venueId, Config.VENUE_OBJECT_TYPES.ORDINAL, { format }),
    listVenuesForAccount: accountId => listVenuesForAccountRequest(accountId, { locale: 'en' }),
    listTranslatedVenuesForAccount: accountId => listVenuesForAccountRequest(accountId),
    listVenueIds: () => listVenueIdsRequest(),
    venueIdIsMigrated: () => ({ isMigrated: true, isEntityMigrated: true, isFeatureMigrated: false }),
    getFeatures: (venueId, levelId, format = 'geoJson') => getFeaturesRequest(venueId, { levelId, format }),
    getEntities: (venueId, accountId, format = 'legacy', locale, baseVenueId) =>
        getEntitiesRequest(venueId, { accountId, format, locale, baseVenueId }),
    getVenueDataObject: (venueId, objectType, format, locale) =>
        getVenueDataObjectRequest(venueId, objectType, { format, locale }),
    getContent: (contentType, cn, category, venueId, accountId, noExtension) =>
        getContentRequest(category, contentType, cn, { venue: venueId, account: accountId, noExtension }),
    listContent: (contentType, category, venueId, accountId, noExtensions, full) =>
        listContentRequest(category, contentType, { venue: venueId, account: accountId, noExtensions, full }),
    putFeaturesAndEntities: (venueId, payload, options = { format: 'legacy' }) =>
        putFeaturesAndEntitiesRequest(venueId, payload, options),
};

export { Rms };
